import React from 'react';
import './Controls.css';

interface ControlsProps {
  percentiles: number[];
  ashLoads: number[];
  seismicIntensities: number[];
  selectedPercentile: number | null;
  selectedAshLoad: number | null;
  selectedSeismicIntensity: number | null;
  selectedSeismicThreshold: string | null;
  thresProb: number;
  mapMode: 'probability' | 'hazard' | 'wms';
  hazardType: 'ash' | 'lava' | 'seismic';
  exposureTime: number;
  opacity: number;
  downloadFormat: 'png' | 'geotiff' | 'geojson';
  hasActiveLayers: boolean;
  bbox: string;
  onPercentileChange: (percentile: number) => void;
  onAshLoadChange: (ashLoad: number) => void;
  onSeismicIntensityChange: (intensity: number) => void;
  onThresProbChange: (thresProb: number) => void;
  onMapModeChange: (mode: 'probability' | 'hazard' | 'wms') => void;
  onHazardTypeChange: (type: 'ash' | 'lava' | 'seismic') => void;
  onExposureTimeChange: (years: number) => void;
  onOpacityChange: (opacity: number) => void;
  onDownloadFormatChange: (format: 'png' | 'geotiff' | 'geojson') => void;
  onBboxChange: (bbox: string) => void;
  onLoadProbabilityData: () => void;
  onLoadHazardData: () => void;
  onLoadLavaData: () => void;
  onLoadSeismicData: () => void;
  onLoadSeismicHazardData: () => void;
  onLoadSeismicWmsData: () => void;
  onSeismicThresholdChange: (threshold: string | null) => void;
  onDownloadLayerData: (format: 'png' | 'geotiff' | 'geojson') => void;
  onClearMapLayers: () => void;
  loading: boolean;
}

const Controls: React.FC<ControlsProps> = ({
  percentiles,
  ashLoads,
  seismicIntensities,
  selectedPercentile,
  selectedAshLoad,
  selectedSeismicIntensity,
  selectedSeismicThreshold,
  thresProb,
  mapMode,
  hazardType,
  exposureTime,
  opacity,
  downloadFormat,
  hasActiveLayers,
  bbox,
  onPercentileChange,
  onAshLoadChange,
  onSeismicIntensityChange,
  onThresProbChange,
  onMapModeChange,
  onHazardTypeChange,
  onExposureTimeChange,
  onOpacityChange,
  onDownloadFormatChange,
  onBboxChange,
  onLoadProbabilityData,
  onLoadHazardData,
  onLoadLavaData,
  onLoadSeismicData,
  onLoadSeismicHazardData,
  onLoadSeismicWmsData,
  onSeismicThresholdChange,
  onDownloadLayerData,
  onClearMapLayers,
  loading
}) => {
  return (
    <aside className="controls">
      <div className="panel-section">
        <h3><i className="fas fa-cogs"></i> Analysis Parameters</h3>
        
        <div className="control-group">
          <label htmlFor="hazard-type-select">
            <i className="fas fa-mountain"></i> Hazard Type:
          </label>
          <select
            id="hazard-type-select"
            className="form-control"
            value={hazardType}
            onChange={(e) => onHazardTypeChange(e.target.value as 'ash' | 'lava' | 'seismic')}
          >
            <option value="ash">Volcanic Ash</option>
            <option value="lava">Lava Flows</option>
            <option value="seismic">Seismic Hazard</option>
          </select>
        </div>

        <div className="control-group">
          <label htmlFor="exposure-time-select">
            <i className="fas fa-clock"></i> Exposure Time:
          </label>
          <select
            id="exposure-time-select"
            className="form-control"
            value={exposureTime}
            onChange={(e) => onExposureTimeChange(parseInt(e.target.value))}
            disabled={true}
          >
            <option value={30}>30 years</option>
          </select>
          <small className="help-text">
            <i className="fas fa-info-circle"></i> Current datasets available for 30-year exposure time only
          </small>
        </div>

        <div className="control-group">
          <label htmlFor="mode-select">
            <i className="fas fa-chart-area"></i> Map Type:
          </label>
          <select
            id="mode-select"
            className="form-control"
            value={mapMode}
            onChange={(e) => onMapModeChange(e.target.value as 'probability' | 'hazard' | 'wms')}
            disabled={hazardType === 'lava'}
          >
            <option value="probability">Exceedance Probability</option>
            <option value="hazard">Hazard Map</option>
            {hazardType === 'seismic' && (
              <option value="wms">Seismic Hazard WMS</option>
            )}
          </select>
          {hazardType === 'lava' && (
            <small className="form-text text-muted">
              Only probability maps available for lava flows
            </small>
          )}
        </div>

        <div className="control-group">
          <label htmlFor="percentile-select">
            <i className="fas fa-percentage"></i> {hazardType === 'lava' ? 'Epistemic Percentile' : 'Statistics'}:
          </label>
          <select
            id="percentile-select"
            className="form-control"
            value={selectedPercentile || ''}
            onChange={(e) => onPercentileChange(Number(e.target.value))}
          >
            {hazardType === 'lava' ? (
              // Percentili hardcoded per lava basati sui layer di GeoServer
              [0, 25, 50, 75].map((percentile) => (
                <option key={percentile} value={percentile}>
                  {percentile === 0 ? '0 (Mean)' : 
                   percentile === 50 ? '50 (Median)' : 
                   `${percentile}th percentile`}
                </option>
              ))
            ) : (
              // Usa i percentili dall'API per ash e seismic
              percentiles.map((percentile) => (
                <option key={percentile} value={percentile}>
                  {percentile === 0 ? 'Mean' : percentile}
                </option>
              ))
            )}
          </select>
        </div>

        {hazardType === 'seismic' && mapMode === 'probability' && (
          <div className="control-group">
            <label htmlFor="intensity-select">
              <i className="fas fa-chart-bar"></i> Macroseismic Intensity:
            </label>
            <select
              id="intensity-select"
              className="form-control"
              value={selectedSeismicIntensity || ''}
              onChange={(e) => onSeismicIntensityChange(Number(e.target.value))}
            >
              {seismicIntensities.map((intensity) => (
                <option key={intensity} value={intensity}>
                  {intensity}
                </option>
              ))}
            </select>
          </div>
        )}

        {mapMode === 'probability' && hazardType === 'ash' && (
          <div className="control-group">
            <label htmlFor="ashload-select">
              <i className="fas fa-cloud"></i> Ash Threshold (kg/m²):
            </label>
            <select
              id="ashload-select"
              className="form-control"
              value={selectedAshLoad || ''}
              onChange={(e) => onAshLoadChange(Number(e.target.value))}
            >
              {ashLoads.map((ashLoad) => (
                <option key={ashLoad} value={ashLoad}>
                  {ashLoad}
                </option>
              ))}
            </select>
          </div>
        )}

        {mapMode === 'hazard' && (
          <div className="control-group">
            <label htmlFor="thres-prob-input">
              <i className="fas fa-chart-line"></i> Probability Threshold:
            </label>
            <input
              type="number"
              id="thres-prob-input"
              className="form-control"
              min="0"
              max="1"
              step="0.01"
              placeholder="e.g. 0.1"
              value={thresProb}
              onChange={(e) => onThresProbChange(Number(e.target.value))}
            />
          </div>
        )}
        
        {hazardType === 'seismic' && mapMode === 'wms' && (
          <div className="control-group">
            <label htmlFor="seismic-threshold-select">
              <i className="fas fa-layer-group"></i> Seismic Threshold (%):
            </label>
            <select
              id="seismic-threshold-select"
              className="form-control"
              value={selectedSeismicThreshold || ''}
              onChange={(e) => onSeismicThresholdChange(e.target.value || null)}
            >
              <option value="">Select Threshold</option>
              <option value="05">5%</option>
              <option value="10">10%</option>
              <option value="20">20%</option>
            </select>
          </div>
        )}
        
        {/* Bounding Box Filter */}
        <div className="control-group">
          <label htmlFor="bbox-input">
            <i className="fas fa-vector-square"></i> Bounding Box (optional):
          </label>
          <input
            type="text"
            id="bbox-input"
            className="form-control"
            placeholder="minLon,minLat,maxLon,maxLat (e.g., 14.5,37.0,15.5,38.0)"
            value={bbox}
            onChange={(e) => onBboxChange(e.target.value)}
            disabled={loading}
          />
          <small style={{ color: '#aaa', fontSize: '10px', marginTop: '4px', display: 'block' }}>
            Leave empty for full extent, or use Draw Tools on map
          </small>
        </div>

        <div className="control-group">
          <button 
            onClick={() => {
              if (hazardType === 'lava') {
                onLoadLavaData();
              } else if (hazardType === 'seismic') {
                if (mapMode === 'probability') {
                  onLoadSeismicData();
                } else if (mapMode === 'hazard') {
                  onLoadSeismicHazardData();
                } else if (mapMode === 'wms') {
                  onLoadSeismicWmsData();
                }
              } else if (mapMode === 'probability') {
                onLoadProbabilityData();
              } else {
                onLoadHazardData();
              }
            }}
            disabled={loading || 
              (hazardType !== 'seismic' || mapMode !== 'wms') && selectedPercentile === null || 
              (hazardType === 'ash' && mapMode === 'probability' && selectedAshLoad === null) ||
              (hazardType === 'seismic' && mapMode === 'probability' && selectedSeismicIntensity === null) ||
              (hazardType === 'seismic' && mapMode === 'wms' && selectedSeismicThreshold === null)}
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> Loading...
              </>
            ) : (
              <>
                <i className="fas fa-search"></i> Execute Query
              </>
            )}
          </button>
        </div>
      </div>

      {hasActiveLayers && (
        <div className="panel-section">
          <h3><i className="fas fa-sliders-h"></i> Display Settings</h3>
          
          <div className="control-group">
            <label htmlFor="opacity-slider">
              <i className="fas fa-adjust"></i> Layer Opacity: {Math.round(opacity * 100)}%
            </label>
            <input
              type="range"
              id="opacity-slider"
              min="0"
              max="1"
              step="0.1"
              value={opacity}
              onChange={(e) => onOpacityChange(Number(e.target.value))}
            />
          </div>
          
          <div className="control-group">
            <label>Download Format:</label>
            <select 
              value={downloadFormat} 
              onChange={(e) => onDownloadFormatChange(e.target.value as 'png' | 'geotiff' | 'geojson')}
              disabled={loading}
            >
              {/* Formati per layer raster (lava WMS) */}
              {hazardType === 'lava' && (
                <>
                  <option value="png">PNG Image</option>
                  <option value="geotiff">GeoTIFF Raster</option>
                </>
              )}
              {/* Solo PNG per seismic WMS */}
              {hazardType === 'seismic' && mapMode === 'wms' && (
                <option value="png">PNG Image</option>
              )}
              {/* Formati per layer vettoriali (ash, seismic non-WMS) */}
              {(hazardType === 'ash' || (hazardType === 'seismic' && mapMode !== 'wms')) && (
                <>
                  <option value="png">PNG Image</option>
                  <option value="geojson">GeoJSON Vector</option>
                  {/* <option value="shapefile">Shapefile (ZIP)</option> */}
                </>
              )}
            </select>
            
            <button 
              className="download-btn"
              onClick={() => onDownloadLayerData(downloadFormat)}
              disabled={loading}
            >
              <i className="fas fa-download"></i> Download Layer Data
            </button>
          </div>
        </div>
      )}

      <div className="panel-section">
        <h3><i className="fas fa-info-circle"></i> Instructions</h3>
        <div className="tool-instructions">
          <p>
            <i className="fas fa-lightbulb"></i> 
            Select parameters and click "Execute Query" to visualize volcanic hazard data on the map.
          </p>
        </div>
        
        <div className="control-group" style={{ marginTop: '16px' }}>
          <button 
            className="clear-btn"
            onClick={onClearMapLayers}
            disabled={loading}
          >
            <i className="fas fa-broom"></i> Clear Map Layers
          </button>
        </div>
      </div>

      <div className="panel-section">
        <h3><i className="fas fa-balance-scale"></i> License</h3>
        <div className="license-info">
          <div className="license-section">
            <h4><i className="fas fa-exclamation-triangle"></i> Liability Claim</h4>
            <p>This tool is provided "as is" without warranty. Users assume full responsibility for any decisions based on this data.</p>
          </div>
          
          <div className="license-section cc-license">
            <div className="cc-container">
              <div className="cc-symbol">
                <i className="fab fa-creative-commons"></i>
                <i className="fab fa-creative-commons-by"></i>
              </div>
              <div className="cc-text">
                <h4>Attribution 4.0 International</h4>
                <p>CC BY 4.0</p>
                <p>Deed</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Controls;